// @flow

import React from 'react';
import _ from 'lodash';
import I18n from 'PMT/i18n/I18n';
import moment from 'moment';

import { RestaurantAction } from 'actions';

import SecurityContext from 'utils/permissions/SecurityContext';
import Listener from 'PMT/flux/Listener';

// -- stores
import { RestaurantStore, SessionStore } from 'stores';

// -- constants
import { Config, Events, Constants } from 'constants';

// -- entities
import Restaurant from 'PMT/entities/Restaurant';
import RestaurantMultipart from 'PMT/entities/RestaurantMultipart';

// -- components
import AbstractComponent from 'components/AbstractComponent';
import Ariane from 'PMT/components/Ariane';
import { Tabs, Tab } from 'react-bootstrap';

import PublicFormView from './components/PublicFormView';
// import OpeningHoursFormView from './components/OpeningHoursFormView';
import LegalFormView from './components/LegalFormView';
import FinanceFormView from './components/FinanceFormView';
import ConfigurationFormView from './components/ConfigurationFormView';
import FeaturesFormView from './components/FeaturesFormView';
import ThemeFormView from './components/ThemeFormView';

export default class EditRestaurantPage extends AbstractComponent {
  static Tab = {
    PUBLIC: 'public',
    MENU: 'menu',
    OPENING_HOURS: 'opening-hours',
    LEGAL: 'legal',
    FINANCE: 'finance',
    FEATURES: 'features',
    CONFIGURATION: 'configuration',
    THEME: 'theme',
    GROUPS: 'groups',
  };

  constructor() {
    super();

    this.arianeIcon = 'fa-user';
    this.arianeDetails = [
      {
        name: 'Paramètres',
        link: null,
      },
    ];
  }

  state = {
    restaurant: null,

    lastUpdateTime: moment().valueOf(),
  };

  componentDidMount() {
    if (!SecurityContext.restaurantSettings.read()) {
      // TODO: redirect
    }
    RestaurantStore.addListeners(this.restaurantStoreListeners);

    this.loadRestaurant();
  }

  componentWillUnmount() {
    RestaurantStore.removeListeners(this.restaurantStoreListeners);
  }

  get restaurantStoreListeners(): Array<Listener> {
    return [
      new Listener(Events.LOAD_RESTAURANT_SUCCESS, this.onRestaurantLoad),
      new Listener(Events.UPDATE_RESTAURANT_SUCCESS, this.onRestaurantUpdate),
      new Listener(Events.UPDATE_RESTAURANT_FAILURE, this.onError),
      new Listener(Events.LOAD_RESTAURANT_FAILURE, this.onError),
    ];
  }

  loadRestaurant = () => {
    this.showLoading();
    RestaurantAction.retrieveCurrentRestaurant();
  };

  // -- Event functions

  onRestaurantLoad = () => {
    const restaurant: Restaurant = RestaurantStore.getRestaurant().copy();

    this.setState({
      restaurant,
      loading: false,
    });
  };

  onRestaurantUpdate = () => {
    const restaurant: Restaurant = RestaurantStore.getRestaurant().copy();

    this.setState({
      restaurant,
      lastUpdateTime: moment().valueOf(),
      loading: false,
      apiError: null,
    });
  };

  onError = () => {
    this.setState({
      apiError: RestaurantStore.getApiError(),
      loading: false,
    });
  };

  // -- Tabs

  onTabChange = activeKey => {
    if (!_.isUndefined(activeKey)) {
      SessionStore.saveEditRestaurantTab(activeKey);

      /*
       * Update the query params.
       */
      this.onChangeQueryUrl({
        tab: activeKey,
      });
    }
  };

  get defaultTabKey(): number {
    if (!_.isNull(this.props.location.query)) {
      const tabQuery = this.props.location.query.tab;
      SessionStore.saveEditRestaurantTab(tabQuery);
      return tabQuery;
    }

    return SessionStore.getEditRestaurantTab();
  }

  // -- Event form
  onUpdate = (restaurantMultipart: RestaurantMultipart) => {
    this.showLoading(() => {
      RestaurantAction.updateRestaurant(restaurantMultipart);
    });
  };

  //
  // Renderers
  //

  renderWarnings() {
    return (
      <div>
        {this.state.restaurant && (this.state.restaurant.status === 'ENABLED' || this.state.restaurant.status === 'DISABLED')
          && this.state.restaurant.legal && this.state.restaurant.legal.bank
          && this.state.restaurant.legal.bank.transferFrequency === 'DISABLED' && (
            <p className="bg-warning" style={{ padding: 16, marginTop: 16, }}>{I18n.tr('restaurant_edit.configuration.transferFrequency.DISABLED.tip')}</p>
          )}

        {this.state.restaurant && this.state.restaurant.status === 'ARCHIVED' && (
          <p className="bg-warning" style={{ padding: 16, marginTop: 16, }}>{I18n.tr('restaurant_edit.configuration.statut.ARCHIVED.tip')}</p>
        )}
      </div>
    )
  }

  renderError() {
    let error = null;
    if (this.state.apiError) {
      switch (this.state.apiError.code) {
        case '20009':
          error = I18n.tr('restaurant_edit.error.20009');
          break;
        default:
          error = I18n.tr('restaurant_edit.error.default');
      }

      return (
        error && <div className="alert alert-danger top-buffer-30">{error}</div>
      );
    }

    return null;
  }

  renderBody() {
    return (
      <div
        className="edit_restaurant_form_view"
        key={this.state.lastUpdateTime}
      >
        <Tabs
          defaultActiveKey={this.defaultTabKey}
          id="edit_restaurant_page__tabs"
          onSelect={this.onTabChange}
        >
          {SecurityContext.restaurantSettings.readPublicInfo() && (
            <Tab
              eventKey={EditRestaurantPage.Tab.PUBLIC}
              title={I18n.tr('restaurant.edit.tabs.public').toUpperCase()}
            >
              {this.renderWarnings()}
              {this.renderError()}
              <div className="top-buffer-30">
                <PublicFormView
                  restaurant={this.state.restaurant}
                  onUpdate={this.onUpdate}
                />
              </div>
            </Tab>
          )}

          {SecurityContext.restaurantSettings.readLegal() && (
            <Tab
              eventKey={EditRestaurantPage.Tab.LEGAL}
              title={I18n.tr('restaurant.edit.tabs.legal').toUpperCase()}
            >
              {this.renderWarnings()}
              {this.renderError()}
              <div className="top-buffer-30">
                <LegalFormView
                  restaurant={this.state.restaurant}
                  onUpdate={this.onUpdate}
                />
              </div>
            </Tab>
          )}

          {SecurityContext.restaurantSettings.readFinance() && (
            <Tab
              eventKey={EditRestaurantPage.Tab.FINANCE}
              title={I18n.tr('restaurant.edit.tabs.finance').toUpperCase()}
            >
              {this.renderWarnings()}
              {this.renderError()}
              <div className="top-buffer-30">
                <FinanceFormView
                  restaurant={this.state.restaurant}
                  onUpdate={this.onUpdate}
                />
              </div>
            </Tab>
          )}

          {SecurityContext.restaurantSettings.readFeatures() && (
            <Tab
              eventKey={EditRestaurantPage.Tab.FEATURES}
              title={I18n.tr('restaurant.edit.tabs.features').toUpperCase()}
            >
              {this.renderWarnings()}
              {this.renderError()}
              <div className="top-buffer-30">
                <FeaturesFormView
                  restaurant={this.state.restaurant}
                  onUpdate={this.onUpdate}
                />
              </div>
            </Tab>
          )}

          {SecurityContext.restaurantSettings.readConfiguration() && (
            <Tab
              eventKey={EditRestaurantPage.Tab.CONFIGURATION}
              title={I18n.tr(
                'restaurant.edit.tabs.configuration'
              ).toUpperCase()}
            >
              {this.renderWarnings()}
              {this.renderError()}
              <div className="top-buffer-30">
                <ConfigurationFormView
                  restaurant={this.state.restaurant}
                  onUpdate={this.onUpdate}
                />
              </div>
            </Tab>
          )}

          {SecurityContext.restaurantSettings.readTheme() && (
            <Tab
              eventKey={EditRestaurantPage.Tab.THEME}
              title={I18n.tr('restaurant.edit.tabs.theme').toUpperCase()}
            >
              {this.renderWarnings()}
              {this.renderError()}
              <div className="top-buffer-30">
                <ThemeFormView
                  restaurant={this.state.restaurant}
                  onUpdate={this.onUpdate}
                />
              </div>
            </Tab>
          )}
        </Tabs>

        <div className="top-buffer-40 text-center">
          {I18n.tr('restaurant.edit.footer')}
          &nbsp;
          <a href={`mailto:${Constants.CONTACT_EMAIL}`}>
            {Constants.CONTACT_EMAIL}
          </a>
        </div>
      </div>
    );
  }

  render() {
    let contentView = null;

    if (this.state.loading) {
      return this.renderLoading();
    }

    if (_.isNull(this.state.restaurant)) {
      contentView = this.renderOnLoadingContent();
    } else {
      contentView = this.renderBody();
    }

    return (
      <div className="edit_restaurant_page">
        <Ariane icon={this.arianeIcon} details={this.arianeDetails} />
        {contentView}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./scripts/pages/restaurant/edit/EditRestaurantPage.js